import React, { useCallback, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UploadIcon, FileIcon, XIcon } from 'lucide-react';
import * as XLSX from 'xlsx';
import { parse as parseCsv } from 'papaparse';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ImportMode } from './StepImportMode';

import { DuplicateStrategy } from '@/services/importService';
import { config } from '@/config/config';
import { formatFileSize } from '@/utils/fileUtils';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useImportTranslations } from '@/hooks/useImportTranslations';
import { logDebug } from '@/utils/errorHandler';

// Define the structure type
type StructureType = 'attribute_groups' | 'attributes' | 'families' | null;

interface StepUploadProps {
  onFileSelected: (file: File, headers: string[], previewData: any[], fullData: any[]) => void;
  onBulkImageSelected?: (images: File[], zip: File | null, overwrite: boolean) => void;
  importMode?: ImportMode;
  structureType?: StructureType;
  onStructureTypeSelect?: (type: StructureType) => void;
  duplicatePolicy?: DuplicateStrategy;
  onDuplicatePolicyChange?: (policy: DuplicateStrategy) => void;
}

const StepUpload: React.FC<StepUploadProps> = ({
  onFileSelected,
  onBulkImageSelected,
  importMode = 'products',
  structureType = null,
  onStructureTypeSelect,
  duplicatePolicy = (config.imports?.defaults?.duplicateStrategy as DuplicateStrategy) ||
    'overwrite',
  onDuplicatePolicyChange,
}) => {
  const [, setPreviewData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Bulk image states
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  // Initialize translations - hooks must be called at top level, not in try-catch
  const { getUploadText, getButtonText, getModeText, getStructureTypeText, t } =
    useImportTranslations();

  // Image validation constants
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
  const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'];
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_ZIP_SIZE = 100 * 1024 * 1024; // 100MB

  const parseFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = e => {
        const result = e.target?.result;
        let previewData: any[] = [];
        let fullData: any[] = []; // Add full data for validation
        let headers: string[] = [];

        if (file.name.endsWith('.csv')) {
          // Parse all data first for validation
          const fullParsed = parseCsv(result as string, {
            header: true,
            skipEmptyLines: true,
            // No preview limit for full data
          });
          fullData = fullParsed.data || [];
          headers = fullParsed.meta?.fields || [];

          // Debug log for development - CSV parsing successful
          logDebug('CSV file parsed successfully', {
            component: 'StepUpload',
            action: 'parseFile',
            headers,
            dataLength: fullData.length,
            meta: fullParsed.meta,
          });

          // Then create limited preview for UI display
          previewData = fullData.slice(0, config.imports?.defaults?.previewRows || 5);
        } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          const workbook = XLSX.read(result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
          headers = (worksheet[0] as string[]) || [];

          // Debug log for development - Excel parsing successful
          logDebug('Excel file parsed successfully', {
            component: 'StepUpload',
            action: 'parseFile',
            headers,
            worksheet: worksheet[0],
            worksheetLength: worksheet.length,
          });

          // Get all data rows (excluding header)
          const allDataRows = (worksheet.slice(1) as any[][]) || [];

          // Convert all rows to object format for validation
          fullData = allDataRows.map((row: any[]) => {
            const rowObj: any = {};
            headers.forEach((header, index) => {
              rowObj[header] = row[index] !== undefined ? row[index] : '';
            });
            return rowObj;
          });

          // Create limited preview for UI display
          previewData = fullData.slice(0, config.imports?.defaults?.previewRows || 5);
        }

        // Debug log for development - File parsing completed
        logDebug('File parsing completed', {
          component: 'StepUpload',
          action: 'parseFile',
          headers,
          previewDataLength: previewData.length,
          fullDataLength: fullData.length,
        });

        setPreviewData(previewData);
        setHeaders(headers);
        setSelectedFile(file);

        // Pass both preview and full data to parent
        onFileSelected(file, headers, previewData, fullData);
      };
      if (file.name.endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    },
    [onFileSelected]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      parseFile(file);
    }
  };

  // Image validation function
  const validateImageFile = (file: File): string | null => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return t('imports.errors.upload.invalidFileType');
    }

    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_IMAGE_EXTENSIONS.includes(extension)) {
      return t('imports.errors.upload.invalidFileType');
    }

    if (file.size > MAX_IMAGE_SIZE) {
      return t('imports.errors.upload.maxFileSize', {
        size: Math.round(MAX_IMAGE_SIZE / 1024 / 1024),
      });
    }

    return null;
  };

  // Handle multiple image files selection
  const handleImageFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach((file, index) => {
      const error = validateImageFile(file);
      if (error) {
        errors.push(`File ${index + 1} (${file.name}): ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      alert(`${t('imports.errors.upload.bulkImages.someFilesRejected')}:\n${errors.join('\n')}`);
    }

    setImageFiles(validFiles);

    // Clear ZIP file if images are selected
    if (validFiles.length > 0) {
      setZipFile(null);
      if (zipInputRef.current) {
        zipInputRef.current.value = '';
      }
    }
  };

  // Handle ZIP file selection
  const handleZipFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const errors: string[] = [];

      if (!file.name.toLowerCase().endsWith('.zip')) {
        errors.push(t('imports.errors.upload.invalidFileType'));
      }

      if (file.size > MAX_ZIP_SIZE) {
        errors.push(
          t('imports.errors.upload.maxFileSize', {
            size: Math.round(MAX_ZIP_SIZE / 1024 / 1024),
          })
        );
      }

      if (errors.length > 0) {
        alert(errors.join('\n'));
        return;
      }

      setZipFile(file);

      // Clear individual images if ZIP is selected
      setImageFiles([]);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  // Remove individual image file
  const removeImageFile = (index: number) => {
    const newFiles = imageFiles.filter((_, i) => i !== index);
    setImageFiles(newFiles);
  };

  // Remove ZIP file
  const removeZipFile = () => {
    setZipFile(null);
    if (zipInputRef.current) {
      zipInputRef.current.value = '';
    }
  };

  // Handle bulk image upload
  const handleBulkImageUpload = () => {
    if (!imageFiles.length && !zipFile) {
      alert(t('imports.errors.upload.noFileSelected'));
      return;
    }

    onBulkImageSelected?.(imageFiles, zipFile, overwriteExisting);
  };

  // Show structure type selection if we're in a structure import mode
  const showStructureTypeSelector = importMode === 'structure';

  // Show duplicate policy only for product imports
  const showDuplicatePolicy = importMode === 'products' && onDuplicatePolicyChange;

  // Render structure type selector
  const renderStructureTypeSelector = () => {
    if (!showStructureTypeSelector) return null;

    const structureTypes = [
      {
        value: 'attribute_groups',
        label: getStructureTypeText('attributeGroups'),
        description: getStructureTypeText('attributeGroupsDescription'),
      },
      {
        value: 'attributes',
        label: getStructureTypeText('attributes'),
        description: getStructureTypeText('attributesDescription'),
      },
      {
        value: 'families',
        label: getStructureTypeText('families'),
        description: getStructureTypeText('familiesDescription'),
      },
    ];

    return (
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-medium">{getStructureTypeText('title')}</h3>
        <RadioGroup
          value={structureType || ''}
          onValueChange={value => onStructureTypeSelect?.(value as StructureType)}
          className="space-y-3"
        >
          {structureTypes.map(type => (
            <Card
              key={type.value}
              className={`border-2 p-4 ${structureType === type.value ? 'border-primary' : 'border-muted'}`}
            >
              <CardContent className="p-0">
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value={type.value} id={type.value} className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor={type.value} className="cursor-pointer text-base font-medium">
                      {type.label}
                    </Label>
                    <p className="mt-1 text-sm text-muted-foreground">{type.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </RadioGroup>
      </div>
    );
  };

  // Render duplicate policy selector
  const renderDuplicatePolicySelector = () => {
    if (!showDuplicatePolicy) return null;

    const strategies = [
      {
        value: 'skip',
        title: getUploadText('duplicateStrategy.skip.title'),
        description: getUploadText('duplicateStrategy.skip.description'),
      },
      {
        value: 'overwrite',
        title: getUploadText('duplicateStrategy.overwrite.title'),
        description: getUploadText('duplicateStrategy.overwrite.description'),
      },
      {
        value: 'abort',
        title: getUploadText('duplicateStrategy.abort.title'),
        description: getUploadText('duplicateStrategy.abort.description'),
      },
    ];

    return (
      <div className="mb-6">
        <h3 className="mb-2 text-lg font-medium">{getUploadText('duplicateStrategy.title')}</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          {getUploadText('duplicateStrategy.description')}
        </p>
        <RadioGroup
          value={duplicatePolicy}
          onValueChange={value => onDuplicatePolicyChange?.(value as DuplicateStrategy)}
          className="space-y-3"
        >
          {strategies.map(strategy => (
            <Card
              key={strategy.value}
              className={`border-2 p-4 ${duplicatePolicy === strategy.value ? 'border-primary' : 'border-muted'}`}
            >
              <CardContent className="p-0">
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value={strategy.value} id={strategy.value} className="mt-1" />
                  <div className="flex-1">
                    <Label
                      htmlFor={strategy.value}
                      className="cursor-pointer text-base font-medium"
                    >
                      {strategy.title}
                    </Label>
                    <p className="mt-1 text-sm text-muted-foreground">{strategy.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </RadioGroup>
      </div>
    );
  };

  // Render bulk image upload section
  const renderBulkImageUpload = () => {
    return (
      <div className="space-y-6">
        <div className="rounded-md bg-muted p-4">
          <h3 className="mb-2 font-medium">{getModeText('bulk_images', 'title')}</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            {getModeText('bulk_images', 'instructions')}
          </p>
        </div>

        {/* Individual Images Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{getButtonText('selectFiles')}</CardTitle>
            <CardDescription>{getUploadText('supportedImageFormats')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => imageInputRef.current?.click()}
                  className="flex items-center space-x-2"
                >
                  <UploadIcon className="size-4" />
                  <span>{getButtonText('selectFiles')}</span>
                </Button>
                <input
                  ref={imageInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageFilesChange}
                  className="hidden"
                />
              </div>

              {imageFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    {getUploadText('bulkImages.filesSelected', {
                      count: imageFiles.length,
                    })}
                  </p>
                  <div className="max-h-32 space-y-1 overflow-y-auto">
                    {imageFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded bg-gray-50 p-2"
                      >
                        <div className="flex items-center space-x-2">
                          <FileIcon className="size-4 text-blue-500" />
                          <span className="truncate text-sm">{file.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {formatFileSize(file.size)}
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeImageFile(index)}
                          className="size-6 p-0"
                        >
                          <XIcon className="size-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* OR Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {getUploadText('bulkImages.orDivider')}
            </span>
          </div>
        </div>

        {/* ZIP Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{getUploadText('zipArchive.title')}</CardTitle>
            <CardDescription>{getUploadText('zipArchive.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => zipInputRef.current?.click()}
                  className="flex items-center space-x-2"
                >
                  <UploadIcon className="size-4" />
                  <span>{getUploadText('zipArchive.selectButton')}</span>
                </Button>
                <input
                  ref={zipInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleZipFileChange}
                  className="hidden"
                />
              </div>

              {zipFile && (
                <div className="flex items-center justify-between rounded bg-gray-50 p-2">
                  <div className="flex items-center space-x-2">
                    <FileIcon className="size-4 text-blue-500" />
                    <span className="text-sm">{zipFile.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {formatFileSize(zipFile.size)}
                    </Badge>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeZipFile}
                    className="size-6 p-0"
                  >
                    <XIcon className="size-3" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Overwrite Option */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="overwrite"
            checked={overwriteExisting}
            onCheckedChange={checked => setOverwriteExisting(checked as boolean)}
          />
          <Label htmlFor="overwrite" className="text-sm">
            {getUploadText('bulkImages.overwriteLabel')}
          </Label>
        </div>

        {/* Upload Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleBulkImageUpload}
            disabled={!imageFiles.length && !zipFile}
            className="flex items-center space-x-2"
          >
            <UploadIcon className="size-4" />
            <span>{getButtonText('upload')}</span>
          </Button>
        </div>
      </div>
    );
  };

  // Render file upload section
  const renderFileUpload = () => {
    return (
      <div className="space-y-6">
        {renderStructureTypeSelector()}

        {(!showStructureTypeSelector || structureType) && (
          <>
            <div className="rounded-md bg-muted p-4">
              <h3 className="mb-2 font-medium">{getUploadText('title')}</h3>
              <p className="mb-4 text-sm text-muted-foreground">{getUploadText('subtitle')}</p>
              <p className="text-xs text-muted-foreground">{getUploadText('supportedFormats')}</p>
            </div>

            <Card>
              <CardContent className="p-6">
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                  <UploadIcon className="mx-auto mb-4 size-12 text-gray-400" />
                  <p className="mt-2 text-lg">{getUploadText('dragDropText')}</p>
                  <p className="mt-1 text-sm text-gray-500">{getUploadText('supportedFormats')}</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-4"
                  >
                    {getUploadText('selectButtonLabel')}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>

                {selectedFile && (
                  <div className="mt-4 rounded-lg bg-gray-50 p-4">
                    <div className="flex items-center space-x-3">
                      <FileIcon className="size-8 text-blue-500" />
                      <div className="flex-1">
                        <p className="font-medium">
                          {getUploadText('fileSelected', {
                            fileName: selectedFile.name,
                          })}
                        </p>
                        <p className="text-sm text-gray-500">
                          {getUploadText('fileSize', {
                            size: formatFileSize(selectedFile.size),
                          })}
                        </p>
                        {(headers?.length || 0) > 0 && (
                          <p className="mt-1 text-sm text-green-600">
                            ✓{' '}
                            {getUploadText('bulkImages.columnsDetected', {
                              count: headers?.length || 0,
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {renderDuplicatePolicySelector()}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {importMode === 'bulk_images' ? renderBulkImageUpload() : renderFileUpload()}
    </div>
  );
};

export default StepUpload;
